import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { cases, hotRanking } from "@/mocks/cases";

interface LatestCasesProps {
  onOpenCase: (id: string) => void;
}

export default function LatestCases({ onOpenCase }: LatestCasesProps) {
  const latest = cases.slice(2, 8);

  return (
    <section id="latest" className="w-full border-y border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          index="02"
          title="最新收录 · 刚刚归档"
          subtitle="编辑部按投稿时间排序整理，每一条都经过情节核实与身份打码后发布。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="flex flex-col gap-4 lg:col-span-2">
            {latest.map((item, index) => (
              <Reveal key={item.id} delay={index * 50}>
                <article className="group relative flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:flex-row sm:p-4">
                  <div className="h-44 w-full shrink-0 overflow-hidden rounded-md sm:h-28 sm:w-44">
                    <img
                      src={item.cover}
                      alt={`${item.categoryName}最新收录配图：${item.title}`}
                      title={`${item.title} · 曝光档案局`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-md bg-accent-100 px-2.5 py-0.5 text-[11px] font-medium text-accent-900">
                        {item.categoryName}
                      </span>
                      <span className="text-[11px] text-foreground-500">{item.date}</span>
                    </div>
                    <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 hidden text-sm leading-relaxed text-foreground-600 sm:block">
                      {item.summary}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-foreground-500">
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-eye-line" />
                        {item.views}
                      </span>
                      {item.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1">
                          <i className="ri-hashtag" />
                          {tag}
                        </span>
                      ))}
                      <span className="ml-auto inline-flex items-center gap-1 font-medium text-primary-700">
                        展开档案
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpenCase(item.id)}
                    aria-label={`查看档案：${item.title}`}
                    className="absolute inset-0 cursor-pointer"
                  />
                </article>
              </Reveal>
            ))}
          </div>

          <aside className="lg:col-span-1">
            <Reveal delay={120}>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-28">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-600 text-background-50">
                    <i className="ri-fire-fill text-lg" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">吃瓜热榜</h3>
                    <p className="text-[11px] text-foreground-500">按本周围观热度排序</p>
                  </div>
                </div>

                <ol className="mt-5 space-y-1">
                  {hotRanking.map((rank, index) => (
                    <li key={rank.id}>
                      <button
                        type="button"
                        onClick={() => onOpenCase(rank.caseId)}
                        className="flex w-full cursor-pointer items-start gap-3 rounded-md px-2 py-3 text-left transition-colors hover:bg-background-100"
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                            index < 3
                              ? "bg-primary-600 text-background-50"
                              : "bg-secondary-100 text-secondary-900"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="flex-1">
                          <span className="block text-sm leading-snug text-foreground-800">{rank.title}</span>
                          <span className="mt-1 flex items-center gap-3 text-[11px] text-foreground-500">
                            <span className="inline-flex items-center gap-1">
                              <i className="ri-eye-line" />
                              {rank.views}
                            </span>
                            <span className="inline-flex items-center gap-1 text-primary-600">
                              <i className="ri-fire-line" />
                              热度 {rank.heat}
                            </span>
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ol>

                <a
                  href="#contact"
                  className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-add-line text-base" />
                  我也要投稿上榜
                </a>
              </div>
            </Reveal>
          </aside>
        </div>
      </div>
    </section>
  );
}